import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { catchError, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { CustomerGroupType, CustomerQuickNote, CustomerQuickNotesKey, FundraisingStatus, Note, Ppm365SubscribeHistoryResponse } from './commonInterfaces';
import { ConfigService } from '../config.service';
import { Customer, CustomerBackgroundProvider, CustomerMarketingDetails, GetTimeZoneResponse, GlInfo, CustomerServiceRecipient, OnTimePaymentIncentive, PotentialGl, CustomerTags } from './commonInterfaces';
import { DataTransform, DateField, DateFieldTypes, NullFieldValues } from './dataTransformFunctions';
import { McDataMapService } from './mc-data-map.service';
import { UiCustomer } from './types/customers';



export interface CreateFromRosterParams {
  newuserId: number,
  tripId: number,
  fundraisingStatus: FundraisingStatus
}

export interface MarketingDetailsUpdatedArgs {
  marketingDetails: CustomerMarketingDetails
}

export const customerDateFields: DateField<Customer>[] =   [
  {
    fieldName: 'timestamp',
    dateType: "utc"
  },
  {
    fieldName: 'renewaltimestamp',
    dateType: "utc"
  }
]


export interface CustomerBackgroundUpdatedArgs {

}

export interface CustomerLogReportData extends Pick<Note, "custId"| "staffId"| "notes"| "submitted"| "logType" | "tripId" | "category"| "inactive" | "contactId" | "dateCompleted" | "dateScheduled"> {
  groupname: string,
  contactName?: string,
  completed: number
}

export interface CustomerLogReportRow {
  staff: string,
  date: string,
  time: string,
  logType: string,
  category: string,
  tripId: number,
  groupId: number,
  groupname: string,
  contactName: string,
  notes: string,
  status: string,
  dateCompleted: Date,
  dateScheduled: Date
}

export interface InactiveTeam {
  tripdate: string;
  destination: string | null;
  tripid: number;
  originalSize: number;
  groupName: string | null;
  custId: number | null;
  originLocation: string | null;
  glName: string | null;
  glEmail: string | null;
  recruiterName: string | null;
  consultantName: string | null;
  datereserved: string | null;
  dropreason: string | null;
  renewal: number | null;
  dropDate: string | null;
  walletBalance: number | null;
  renewalString?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private mcDataMapService: McDataMapService
  ) { }

  customerDateFields = customerDateFields

  customerUpdated = new Subject<number>
  
  private customerServiceMessageSubject = new Subject<{responseToMessageId?: number}>()
  customerServiceMessage$ = this.customerServiceMessageSubject.asObservable()

  private marketingDetailsUpdated = new Subject<MarketingDetailsUpdatedArgs>()
  marketingDetailsUpdated$ = this.marketingDetailsUpdated.asObservable()

  private customerBackgroundProviderUpdated = new Subject<CustomerBackgroundUpdatedArgs>()
  customerBackgroundProviderUpdated$ = this.customerBackgroundProviderUpdated.asObservable()

  readonly ppm365SupportHistoryDateFields: DateField<Ppm365SubscribeHistoryResponse>[] = [
    {fieldName: 'startDate', dateType: 'utc'},
    {fieldName: 'endDate', dateType: 'utc'}
  ]

  readonly customerQuickNotesDateFields: DateField<CustomerQuickNote>[] = [
    {fieldName: 'lastUpdated', dateType: 'utc'}
  ]


  emptyCustomer(): Customer {
    return {
      id: 0,
      groupname: "Loading",
      timestamp: new Date(1000, 0, 1),
      renewaltimestamp: new Date(1000, 0, 1),
      fundraisingStatus: 0,
      address1: "",
      address2: "",
      recruiterId: 0,
      assistId: 0,
      attn: "",
      billingaddress: "",
      churchphone: "",
      city: "",
      country: "",
      countryinterest :0,
      customerstatus: "",
      dayphone: "",
      denomination: "",
      donorRecruiterId: 0,
      email: "",
      fax: "",
      fmsc: "0",
      imported: 0,
      individual: "0",
      mailinglist: 0,
      status: 0,
      groupType: CustomerGroupType.Church,
      tripsEnabled: 1
      
    }
  }

  getCustomer(customerId: number) {
    return this.http.get<Customer>(`${this.config.dataApiUrl}/customers/${customerId.toString()}?includeTags=true`).pipe(
      switchMap(customer => {
        return DataTransform.replaceDates<Customer>(
          customer,
         this.customerDateFields
        )
      })
    )
  }

  getCustomerChildren(customerId: number): Observable<Customer[]> {
    return this.http.get<Customer[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/children`)
  }

  createNewLead(partialCustomer: Partial<Customer>) {
    const defaultCustomer = this.emptyCustomer();
    const newCustomer = { ...defaultCustomer, ...partialCustomer };

    return this.http.post<Customer>(`${this.config.dataApiUrl}/customer/newleads`, newCustomer);
}

  getCustomerMarketingDetails(customerId: number) {
    return this.http.get<CustomerMarketingDetails>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/marketing-details`)
  }

  updateCustomerMarketingDetails(customerId: number, details: Partial<CustomerMarketingDetails>) {
    return this.http.patch<CustomerMarketingDetails>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/marketing-details`, details).pipe(
      tap(details => {
        this.marketingDetailsUpdated.next({marketingDetails: details})
      })
    )
  }

  updateCustomer(customerId: number, customerData: Partial<Customer> & { tagIds?: number[] }) {
    return this.http.patch<Customer>(`${this.config.dataApiUrl}/customers/${customerId.toString()}`, customerData).pipe(
      tap(() => {
        this.customerUpdated.next(customerId);
      })
    )
  }

  getTimeInfo(customerId: number): Observable<GetTimeZoneResponse> {
    return this.http.get<GetTimeZoneResponse>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/local-time`)
  }

  getCurrentDateTime(customerId: number): Observable<DateTime> {
    return this.getTimeInfo(customerId).pipe(
      switchMap(timeInfo => {
        if (timeInfo.zoneName) {
          return of (DateTime.now().setZone(timeInfo.zoneName))
        }
        else {
          const defaultDate = new Date(1000, 0, 1)
          return of(DateTime.fromJSDate(defaultDate))
        }
       
      }),
      catchError((err, caught) => {
        const defaultDate = new Date(1000, 0, 1)
        return of(DateTime.fromJSDate(defaultDate))
      }),
  
    )
  }

  getPotentialGls(customerId: number): Observable<PotentialGl[]> {
    return this.http.get<PotentialGl[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/potential-group-leaders`)
  }

  getGls(customerId: number): Observable<GlInfo[]> {
    return this.http.get<GlInfo[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/group-leaders`)
  }

  getCustomerServiceRecipients(customerId: number): Observable<CustomerServiceRecipient[]> {
    return this.http.get<CustomerServiceRecipient[]>(
      `${this.config.dataApiUrl}/customers/${customerId.toString()}/customer-service-recipients`
    ).pipe(
      map((recipients) => recipients.map((recipient) => ({
        ...recipient,
        trips: recipient.trips.map((trip) => ({
          ...trip,
          tripDate: new Date(trip.tripDate),
        })),
      })))
    );
  }

  getOnTimeIncentiveStatus(customerId: number): Observable<OnTimePaymentIncentive[]> {
    return this.http.get<OnTimePaymentIncentive[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/on-time-incentive-status`).pipe(
      switchMap(incentives => {
        return DataTransform.replaceDatesArray<OnTimePaymentIncentive>(incentives, [{dateType: "central", fieldName: "expiresOn"}])
      })
    )
  }

  getBackgroundCheckProviders(customerId: number): Observable<CustomerBackgroundProvider[]> {
    return this.http.get<CustomerBackgroundProvider[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/background-check-providers`)
  }

  sendCustomerServiceMessage(params: {custId: number, newuserId: number, notes: string, tripId?: number, responseToMessageId?: number}) {

    return this.http.post(`${this.config.dataApiUrl}/customer-log/customer-service-message`, params).pipe(
      tap(() => {
        this.customerServiceMessageSubject.next({responseToMessageId: params.responseToMessageId})
      })
    )
  }

  getCustomerLogReportData(args: {startDate: DateTime, endDate: DateTime, staffId: number}) {
    const params: Record<string, string> = {
      startDate: args.startDate.toISO() ?? '',
      endDate: args.endDate.toISO() ?? '',
    };

    if (args.staffId > 0) {
      params['staffId'] = String(args.staffId);
    }

    return this.http.get<CustomerLogReportData[]>(`${this.config.dataApiUrl}/customers/customer-log-report`, {params}).pipe(
      switchMap(logs => {
       return DataTransform.replaceDatesArray(logs, [{dateType: "central", fieldName: "submitted"}, {dateType: "central", fieldName: "dateCompleted"}, {dateType: "central", fieldName: "dateScheduled"}])
      })
    )
  }

  getCustomerAddressString(customer: Customer): string {
    return `${customer.address1} ${customer.address2} ${customer.city} ${customer.stateprov} ${customer.zipcode} ${customer.country}`
  }

  getCustomerShippingAddressString(customer: Customer): string {
    return `${customer.shipaddress1} ${customer.shipaddress2} ${customer.shipcity} ${customer.shipstateprov} ${customer.shipzipcode} ${customer.shipcountry}`
  }

  getCustomersFromIds(ids: number[]): Observable<Customer[]> {
    return this.http.get<Customer[]>(`${this.config.dataApiUrl}/customers/from-list`, {params: {ids}}) || []
  }

  getCustomersForSearch(searchTerm: string): Observable<UiCustomer[]> {
    return this.http.get<UiCustomer[]>(`${this.config.dataApiUrl}/customers/search`, {params: {search: searchTerm}})
  }

  getInactiveTeams(year: number): Observable<InactiveTeam[]> {
    return this.http.get<InactiveTeam[]>(`${this.config.dataApiUrl}/customers/inactive-teams`, {params: {year}})
  }

  getActiveTags(): Observable<CustomerTags[]> {
    return this.http.get<CustomerTags[]>(`${this.config.dataApiUrl}/customers/tags/available`)
  }

  getPpm365SupportHistory(customerId: number): Observable<Ppm365SubscribeHistoryResponse[]> {
    return this.http.get<Ppm365SubscribeHistoryResponse[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/ppm365-support-history`).pipe(
      switchMap(history => {
        return DataTransform.replaceDatesArray(history, this.ppm365SupportHistoryDateFields)
      })
    )
  }

  getQuickNotes(customerId: number, key?: CustomerQuickNotesKey): Observable<CustomerQuickNote[]> {

    let params: HttpParams = new HttpParams();
    if (key) {
      params = params.set('key', key);
    }

    return this.http.get<CustomerQuickNote[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/quick-notes`, {params: params}).pipe(
      switchMap(notes => {
        return DataTransform.replaceDatesArray(notes, this.customerQuickNotesDateFields)
      })
    )
  }

  saveQuickNote(customerId: number, key: CustomerQuickNotesKey, data: string): Observable<CustomerQuickNote> {
    return this.http.post<CustomerQuickNote>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/quick-notes`, {key: key, data: data})
  }

  createDonorFromRoster(params: CreateFromRosterParams) {
    return this.http.post<Customer>(`${this.config.dataApiUrl}/customers/create-donor-from-roster`, params)
  }

}
